import React, { useEffect, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { compose } from 'redux';
import { injectIntl } from '../../util/reactIntl';
import { Form, AddImages, AddImagesField, Alert } from '../../components';
import { DEFAULT_VARIANT, discardAssetsError, SQUARE_SMALL_2X } from '../../ducks/Assets.duck';
import { useAssetsToSdk } from '../../hooks/useAssetsToSdk';
import css from './Gallery.css';
import { uploadGalleryAssetsHOC } from '../../util/contextHelpers';
import { GALLERY_FILES } from '../../marketplace-custom-config';
import { UploadImageWizard } from '../../components/UploadImageWizard/UploadImageWizard';
import { StrategySelected as Strategy } from '../../components/UploadImageWizard/modals/StrategyModal';
import { StrategiesListModal as List } from '../../components/UploadImageWizard/modals/StrategiesListModal';
import { STEP_UPLOAD_PIC_LIST } from '../../components/UploadImageWizard/helpers';
import { blobsToFileList, blobToFile, convertSingleFileToMult } from '../../util/file';
import { useIsMobile } from '../../hooks/useIsMobile';
import { IconCameraL } from '../../icons';
import { useDispatch, useSelector } from 'react-redux';
import { getAssetErrorMessageFromCode } from '../../util/errors';

const scrollToTop = () =>
    setTimeout(() => {
        window.scrollTo({ behavior: 'smooth', top: 0 });
    }, 1000);

const Gallery = ({
    currentUser,
    onImageUploadHandler: onImageUploadHandlerProp,
    onRemovingGalleryAssets,
    onArrangingGalleryImages,
    onAssetsRequest,
    uploadInProgress,
    children,
    intl,
    location,
    uploadAssetsInProgress,
    assetsData,
    ...restProps
}) => {
    const dispatch = useDispatch();
    const [isMobile] = useIsMobile(1024);

    const [dragOverFORM, setDragOverFORM] = useState(false);
    const [imageIdDragged, setImageIdDragged] = useState(null);

    const discardGalleryError = errorData => dispatch(discardAssetsError(errorData));

    const entityId = currentUser && currentUser.id ? currentUser.id.uuid : null;

    const assetsUploadError = useSelector(s => {
        const errors = s.Assets.assetsErrors;
        return errors && errors[entityId];
    });

    const loading = uploadAssetsInProgress || uploadInProgress;
    const assets =
        assetsData &&
        assetsData[entityId] &&
        (assetsData[entityId][DEFAULT_VARIANT] || assetsData[entityId][SQUARE_SMALL_2X]);

    /** include all assets, not only gallery ones */
    const sdkAssets = useAssetsToSdk({
        assets,
        inProgress: uploadAssetsInProgress,
        allowedTypes: ['image', 'video'],
    });

    const images = (sdkAssets || []).filter(a => a.__type === GALLERY_FILES);

    useEffect(() => {
        assetsUploadError && scrollToTop();
    }, [assetsUploadError]);

    useEffect(() => {
        entityId && onAssetsRequest(entityId);
    }, [entityId]);

    if (!currentUser) {
        return null;
    }

    const arrangeGalleryImages = async ({ images }) => {
        const { isSuccessful, hasVideo } = await onArrangingGalleryImages({
            images,
            assets,
            entityId,
        });
        if (isSuccessful) {
            restProps.onSubmit({ hasVideo, hasGalleryAssets: !!images.length });
        }
    };

    const onImageRemoveHandler = async ({ uuid: idToDelete }) => {
        const { isSuccessful, assets } = await onRemovingGalleryAssets({
            images,
            idToDelete,
            entityId,
        });

        if (!isSuccessful) return false;

        await arrangeGalleryImages({ images: assets });

        return true;
    };

    const onImageUploadHandler = async files => {
        const { isSuccessful, hasVideo } = await onImageUploadHandlerProp({
            files,
            images,
            entityId,
        });

        if (isSuccessful) {
            restProps.onSubmit({
                hasVideo,
                hasGalleryAssets: true,
            });
        }
    };

    const onFormDragHandler = (e, flag) => {
        // function responsible for changing label background
        e.preventDefault();
        e.stopPropagation();

        // do not highlight if new images are dragged over an existing one;
        if (imageIdDragged) return;

        setDragOverFORM(flag);
    };

    const onFormDropHandler = e => {
        setDragOverFORM(false);

        const droppedFiles = e.dataTransfer && e.dataTransfer.files;

        if (!droppedFiles) return;

        onImageUploadHandler(droppedFiles);
    };

    const noAssets = !uploadAssetsInProgress && (!images || images.length === 0);

    return (
        <FinalForm
            {...restProps}
            render={fieldRenderProps => {
                const { form, handleSubmit } = fieldRenderProps;

                const onFilesUpload = (files, { setStrategy, setUploadState }) => {
                    for (let file of files) {
                        form.change(`galleryImages`, file);
                    }
                    onImageUploadHandler(files);

                    setStrategy(null);
                    setUploadState(null);
                };

                const uploadFbPic = (blobs, setters) =>
                    blobsToFileList(blobs, files => onFilesUpload(files, setters));

                const uploadCameraPhoto = (blob, setters) =>
                    convertSingleFileToMult(blobToFile(blob).get('file'), files =>
                        onFilesUpload(files, setters)
                    );

                const fbStrategyProps = {
                    isMultiple: true,
                    onImageUpload: uploadFbPic,
                    inputProps: { maxLength: 10 },
                };

                return (
                    <Form
                        onSubmit={handleSubmit}
                        onDragOver={e => onFormDragHandler(e, true)}
                        onDragEnter={e => onFormDragHandler(e, true)}
                        onDragLeave={e => onFormDragHandler(e, false)}
                        onDragEnd={e => onFormDragHandler(e, false)}
                        onDrop={e => onFormDropHandler(e)}
                    >
                        <h3 className={css.collectInfoHeader}>Fotos und Videos hochladen</h3>
                        <p className={css.collectInfoDesc}>
                            Füge aussagekräftigen Fotos und Videos zu deinem Profil hinzu, um dein
                            reiterliches Können zu zeigen.
                        </p>

                        {assetsUploadError && (
                            <Alert
                                rootClassName={css.errorAlert}
                                closeClassName={css.errorAlertCloseCTA}
                                closeCallback={() => {
                                    if (assetsUploadError) {
                                        discardGalleryError(assetsUploadError);
                                    }
                                }}
                                closeAllowed
                                type="failure"
                            >
                                <p>
                                    {typeof assetsUploadError === 'object' && assetsUploadError.code
                                        ? getAssetErrorMessageFromCode(assetsUploadError.code)
                                        : 'Something went wrong. Please try again later.'}
                                </p>
                            </Alert>
                        )}
                        <AddImages
                            useControls
                            images={images}
                            loading={loading}
                            entityId={entityId}
                            initialState={noAssets}
                            abortRedirect={false}
                            videoControls={false}
                            className={css.addImagesRoot}
                            wrapperClassName={css.threeToTwoWrapper}
                            savedImageAltText="gallery image"
                            onRemoveImage={imageId => onImageRemoveHandler(imageId)}
                            onSubmit={images => arrangeGalleryImages(images)}
                            notifyOnImageDragOver={imageId => setImageIdDragged(imageId)}
                        >
                            <UploadImageWizard
                                uploadInProgress={uploadInProgress || uploadAssetsInProgress}
                                imageName="galleryFile"
                                onImageUpload={convertSingleFileToMult}
                                renderPlaceholder={props => (
                                    <AddImagesField
                                        name="galleryImages"
                                        initialState={noAssets}
                                        dragOverFORM={dragOverFORM}
                                        disabled={uploadInProgress || uploadAssetsInProgress}
                                        inProgress={uploadAssetsInProgress}
                                        imagesUploaded={images.length}
                                        init={() => props.setUploadState(STEP_UPLOAD_PIC_LIST)}
                                        multiple
                                    />
                                )}
                            >
                                <List>
                                    <List.Heading>
                                        <h3>Fotos und Videos hochladen</h3>
                                        <p>
                                            Füge aussagekräftigen Fotos und Videos zu deinem Profil
                                            hinzu, um dein reiterliches Können zu zeigen.
                                        </p>
                                    </List.Heading>
                                    <List.Container>
                                        {isMobile ? (
                                            <List.PcImage
                                                label="Foto oder Video machen"
                                                onPcFileUpload={onFilesUpload}
                                                icon={<IconCameraL />}
                                                // inputProps={{ capture: 'environment' }}
                                                key="camera-photo"
                                                fieldName="camera-photo"
                                            />
                                        ) : (
                                            <List.CamerPhoto label="Foto machen" />
                                        )}
                                        <List.PcImage
                                            label="Foto oder Video hochladen"
                                            onPcFileUpload={onFilesUpload}
                                            isMultiple
                                            key="default-upload"
                                            fieldName="default-upload"
                                        />
                                        <List.FacebookPic />
                                        {/* <List.InstagramPic /> */}
                                    </List.Container>
                                </List>
                                <Strategy>
                                    <Strategy.CameraPhoto
                                        onImageUpload={uploadCameraPhoto}
                                        convertTo="toBlob"
                                    />
                                    <Strategy.Facebook {...fbStrategyProps} />
                                    {/* <Strategy.Instagram {...fbStrategyProps} /> */}
                                </Strategy>
                            </UploadImageWizard>
                        </AddImages>
                    </Form>
                );
            }}
        />
    );
};

export default compose(injectIntl)(uploadGalleryAssetsHOC(Gallery));
